import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import {
  ArchiveBoxIcon,
  ArrowPathIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  EllipsisHorizontalIcon,
  LightBulbIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Tooltip } from '@mui/material';
import { ConversationStatus, UserTypes } from 'shared';

import { Badge } from '../../../../common/Atoms/Badge';
import { Button } from '../../../../common/Atoms/Button';
import { IOption, MultiSelect } from '../../../../common/Atoms/MultiSelect';
import { Notification } from '../../../../common/Atoms/Notification';
import { Toggle } from '../../../../common/Atoms/Toggle';
import { Paragraph } from '../../../../common/Atoms/Typography/Paragraph';
import { classNames } from '../../../../common/lib/classNames';
import { LoadingOverlay } from '../../Molecules/LoadingOverlay';
import { WrappedLink } from '../../WrappedLink';
import { useCurrentOrgUsers } from '../../../hooks/useCurrentOrgUsers';
import { useGetSelectedOrganisation } from '../../../hooks/useGetSelectedOrganisation';
import {
  AgentConversation,
  AgentConversationNote,
  AgentConversationSortBy,
  AgentConversationSortDir,
  AgentMessage,
  AgentRun,
  AgentTask,
  useArchiveAgentConversationMutation,
  useCheckResolvedMutation,
  useDecideTaskMutation,
  useGetAgentConversationQuery,
  useGetAgentConversationsQuery,
  useGetAgentSettingsQuery,
  useResolveConversationMutation,
  useUnarchiveAgentConversationMutation,
  useUpdateAgentSettingsMutation,
} from '../../../services/api/aiAgentApi/aiAgent';
import {
  describeToolActivity,
  friendlyTriggerType,
  getConversationStatusMeta,
  parseConversationSummary,
  ToolActivityMedium,
} from '../lib/agentDisplay';
import { open as openActionDrawer } from '../../../slices/actionDrawer';

import { ConversationListItem } from './ConversationListItem';
import {
  conversationHasFailure,
  conversationNeedsApproval,
  formatLocalTimestamp,
  getRunStatusMeta,
} from './conversationHelpers';
import { ContactAvatar } from './ContactAvatar';
import { RESOLVE_CHECK_TERMINAL_RUN_STATUSES, SHOW_RUNS } from './constants';
import { PendingTaskApproval } from './PendingTaskApproval';
import {
  buildEditedArgsForTask,
  getEditableFieldsForTool,
  getInitialTaskEditValues,
  TaskEditValues,
} from './taskEditHelpers';
import { TranscriptItem } from './TranscriptItem';
import { ConversationComposer } from './ConversationComposer';
import { OriginalOutboundMessage } from './OriginalOutboundMessage';

type StatusFilterValue = AgentConversation[`status`] | `failed` | `archived`;
type SortOptionValue = `${AgentConversationSortBy}:${AgentConversationSortDir}`;
const CLEAR_ALL_STATUS_FILTERS_VALUE = `__clear_all_status_filters__`;

const STATUS_FILTER_OPTIONS: Array<{ value: StatusFilterValue; label: string }> = [
  { value: ConversationStatus.OPEN, label: `Open` },
  { value: ConversationStatus.AWAITING_APPROVAL, label: `Needs approval` },
  { value: ConversationStatus.AWAITING_INPUT, label: `Waiting on customer` },
  { value: ConversationStatus.NEEDS_HUMAN, label: `Needs a human` },
  { value: ConversationStatus.CLOSED, label: `Closed` },
  { value: `failed`, label: `Failed action` },
  { value: ConversationStatus.RESOLVED, label: `Resolved` },
  { value: `archived`, label: `Archived` },
];
const DEFAULT_STATUS_FILTERS: StatusFilterValue[] = STATUS_FILTER_OPTIONS
  .filter(option => option.value !== ConversationStatus.RESOLVED && option.value !== `archived`)
  .map(option => option.value);

type MessageTranscriptItem = {
  kind: `message`;
  key: string;
  created_at: string;
  message: AgentMessage;
  medium?: ToolActivityMedium;
  customerLabel?: string;
  activity?: ReturnType<typeof describeToolActivity>;
  task?: AgentTask;
  inboundEmailId?: string | null;
  attachments: AgentRun[`attachments`];
};

type NoteTranscriptItem = {
  kind: `note`;
  key: string;
  created_at: string;
  note: AgentConversationNote;
};

export function AgentsConversations() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { conversationId: conversationIdParam } = useParams<{ conversationId?: string }>();
  const currentOrg = useGetSelectedOrganisation();
  const organisationId = currentOrg?.id;

  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(conversationIdParam || null);
  const [sortBy, setSortBy] = useState<AgentConversationSortBy>(`last_activity_at`);
  const [sortDir, setSortDir] = useState<AgentConversationSortDir>(`desc`);
  const [statusFilters, setStatusFilters] = useState<StatusFilterValue[]>(DEFAULT_STATUS_FILTERS);
  const [isDecisionPanelOpen, setIsDecisionPanelOpen] = useState(false);
  const [pendingDecisionTaskId, setPendingDecisionTaskId] = useState<string | null>(null);
  const [processingTaskIds, setProcessingTaskIds] = useState<Set<string>>(new Set());
  const [pendingResolveCheckRunId, setPendingResolveCheckRunId] = useState<string | null>(null);
  const [isConversationActionsMenuOpen, setIsConversationActionsMenuOpen] = useState(false);
  const [taskEdits, setTaskEdits] = useState<Record<string, TaskEditValues>>({});
  const [resolvingListConversationId, setResolvingListConversationId] = useState<string | null>(null);
  const conversationActionsMenuRef = useRef<HTMLDivElement | null>(null);
  const transcriptBottomAnchorRef = useRef<HTMLDivElement | null>(null);
  const autoScrolledConversationIdRef = useRef<string | null>(null);
  const currentOrgUsers = useCurrentOrgUsers(false, [UserTypes.Inherit, UserTypes.Invited]);
  const [resolveConversation, { isLoading: isResolvingConversation }] = useResolveConversationMutation();
  const [archiveConversation, { isLoading: isArchivingConversation }] = useArchiveAgentConversationMutation();
  const [unarchiveConversation, { isLoading: isUnarchivingConversation }] = useUnarchiveAgentConversationMutation();
  const [checkResolved, { isLoading: isCheckingResolved }] = useCheckResolvedMutation();
  const [updateAgentSettings, { isLoading: isUpdatingAgentSettings }] = useUpdateAgentSettingsMutation();
  const statusFilterOptionValues = useMemo(() => new Set(STATUS_FILTER_OPTIONS.map(option => option.value)), []);
  const isDefaultStatusFilters = useMemo(() => {
    if (statusFilters.length !== DEFAULT_STATUS_FILTERS.length) {
      return false;
    }

    const defaultSet = new Set(DEFAULT_STATUS_FILTERS);

    return statusFilters.every(value => defaultSet.has(value));
  }, [statusFilters]);
  const statusFilterOptions: IOption[] = useMemo(() => {
    return [
      {
        label: `Clear all`,
        value: CLEAR_ALL_STATUS_FILTERS_VALUE,
      },
      ...STATUS_FILTER_OPTIONS.map(option => ({
        label: option.label,
        value: option.value,
      })),
    ];
  }, []);
  const statusFilterDisplay = useMemo(() => `${statusFilters.length} selected`, [statusFilters.length]);
  const updateStatusFilters = (next: Array<StatusFilterValue | typeof CLEAR_ALL_STATUS_FILTERS_VALUE>) => {
    if (next.includes(CLEAR_ALL_STATUS_FILTERS_VALUE)) {
      setStatusFilters([]);

      return;
    }

    const validNext = next.filter((value): value is StatusFilterValue => {
      return value !== CLEAR_ALL_STATUS_FILTERS_VALUE && statusFilterOptionValues.has(value);
    });
    const dedupedNext = Array.from(new Set(validNext));

    setStatusFilters(dedupedNext);
  };

  const {
    data: agentSettings,
    isLoading: isAgentSettingsLoading,
  } = useGetAgentSettingsQuery({
    organisationId: organisationId || ``,
  }, {
    skip: !organisationId,
  });

  const {
    data: conversationsResponse,
    isLoading: isConversationsLoading,
    error: conversationsError,
  } = useGetAgentConversationsQuery({
    organisationId: organisationId || ``,
    page,
    pageSize,
    sortBy,
    sortDir,
    statuses: statusFilters,
  }, {
    skip: !organisationId,
    pollingInterval: 15000,
    skipPollingIfUnfocused: true,
  });

  const conversations = useMemo(() => conversationsResponse?.conversations || [], [conversationsResponse?.conversations]);
  const pagination = conversationsResponse?.pagination;

  const needsApprovalCount = useMemo(() => conversations.filter(conversationNeedsApproval).length, [conversations]);
  const needsHumanCount = useMemo(() => {
    return conversations.filter(agentConversation => agentConversation.status === ConversationStatus.NEEDS_HUMAN).length;
  }, [conversations]);
  const failedCount = useMemo(() => conversations.filter(conversationHasFailure).length, [conversations]);

  const toConversationsPath = (conversationId?: string) => {
    if (!organisationId) {
      return null;
    }

    return `/${organisationId}/agents/conversations${conversationId ? `/${conversationId}` : ``}`;
  };

  const selectConversation = (conversationId: string | null, replace = false) => {
    setSelectedConversationId(conversationId);

    const nextPath = toConversationsPath(conversationId || undefined);

    if (!nextPath || location.pathname === nextPath) {
      return;
    }

    navigate(nextPath, { replace });
  };

  useEffect(() => {
    setPage(1);
  }, [sortBy, sortDir, statusFilters]);

  useEffect(() => {
    setSelectedConversationId(conversationIdParam || null);
  }, [conversationIdParam]);

  useEffect(() => {
    setIsDecisionPanelOpen(false);
    setPendingResolveCheckRunId(null);
    setProcessingTaskIds(new Set());
    setIsConversationActionsMenuOpen(false);
  }, [selectedConversationId]);

  useEffect(() => {
    if (!isConversationActionsMenuOpen) {
      return;
    }

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target;

      if (target instanceof Node && !conversationActionsMenuRef.current?.contains(target)) {
        setIsConversationActionsMenuOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === `Escape`) {
        setIsConversationActionsMenuOpen(false);
      }
    };

    document.addEventListener(`mousedown`, handleMouseDown);
    document.addEventListener(`keydown`, handleEscape);

    return () => {
      document.removeEventListener(`mousedown`, handleMouseDown);
      document.removeEventListener(`keydown`, handleEscape);
    };
  }, [isConversationActionsMenuOpen]);

  useEffect(() => {
    if (!conversations.length) {
      if (selectedConversationId) {
        selectConversation(null, true);
      }
      else {
        setSelectedConversationId(null);
      }

      return;
    }

    if (!selectedConversationId || !conversations.some(agentConversation => agentConversation.id === selectedConversationId)) {
      selectConversation(conversations[0].id, true);
    }
  }, [conversations, selectedConversationId]);

  const {
    data: conversationDetail,
    isLoading: isConversationLoading,
    error: conversationError,
  } = useGetAgentConversationQuery({
    organisationId: organisationId || ``,
    conversationId: selectedConversationId || ``,
  }, {
    skip: !organisationId || !selectedConversationId,
    pollingInterval: 2000,
    skipPollingIfUnfocused: true,
  });

  useEffect(() => {
    if (!selectedConversationId) {
      autoScrolledConversationIdRef.current = null;

      return;
    }

    if (!conversationDetail || autoScrolledConversationIdRef.current === selectedConversationId) {
      return;
    }

    transcriptBottomAnchorRef.current?.scrollIntoView({
      block: `end`,
    });

    autoScrolledConversationIdRef.current = selectedConversationId;
  }, [conversationDetail, selectedConversationId]);

  const [decideTask, { isLoading: isDecidingTask }] = useDecideTaskMutation();

  const pendingTasks = useMemo(() => {
    return (conversationDetail?.tasks || []).filter(task => task.approval_status === `pending`);
  }, [conversationDetail?.tasks]);

  useEffect(() => {
    setProcessingTaskIds(prev => {
      if (!prev.size) {
        return prev;
      }

      const tasksById = new Map((conversationDetail?.tasks || []).map(task => [task.id, task]));
      const next = new Set<string>();
      let changed = false;

      prev.forEach(taskId => {
        const task = tasksById.get(taskId);

        if (task?.approval_status === `pending`) {
          next.add(taskId);
        }
        else {
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [conversationDetail?.tasks]);

  useEffect(() => {
    setTaskEdits(prev => {
      const pendingTaskIds = new Set(pendingTasks.map(task => task.id));
      const next: Record<string, TaskEditValues> = {};
      let changed = false;

      Object.keys(prev).forEach(taskId => {
        if (pendingTaskIds.has(taskId)) {
          next[taskId] = prev[taskId];
        }
        else {
          changed = true;
        }
      });

      pendingTasks.forEach(task => {
        const editableFields = getEditableFieldsForTool(task.tool_name);

        if (!editableFields.length || next[task.id]) {
          return;
        }

        next[task.id] = getInitialTaskEditValues(task);
        changed = true;
      });

      return changed ? next : prev;
    });
  }, [pendingTasks]);

  const failedRuns = useMemo(() => {
    return (conversationDetail?.runs || []).filter(run => run.status === `failed` && run.error);
  }, [conversationDetail?.runs]);

  const runsById = useMemo(() => {
    return new Map((conversationDetail?.runs || []).map(run => [run.id, run]));
  }, [conversationDetail?.runs]);

  const sortedRuns = useMemo(() => {
    const runs = [ ...(conversationDetail?.runs || []) ];

    runs.sort((a, b) => {
      const aDate = a.started_at || a.created_at;
      const bDate = b.started_at || b.created_at;

      return new Date(bDate).getTime() - new Date(aDate).getTime();
    });

    return runs;
  }, [conversationDetail?.runs]);

  const latestResolveCheckRun = useMemo(() => {
    const resolveCheckRuns = (conversationDetail?.runs || []).filter(run => run.trigger_type === `resolve_check`);

    if (!resolveCheckRuns.length) {
      return null;
    }

    return [ ...resolveCheckRuns ].sort((a, b) => {
      const aDate = a.started_at || a.created_at;
      const bDate = b.started_at || b.created_at;

      return new Date(bDate).getTime() - new Date(aDate).getTime();
    })[0];
  }, [conversationDetail?.runs]);

  const resolveCheckAssistantMessages = useMemo(() => {
    if (!latestResolveCheckRun) {
      return [];
    }

    return (conversationDetail?.transcript || []).filter(message => {
      if (message.run_id !== latestResolveCheckRun.id) {
        return false;
      }

      if ((message.item_type !== `message` && message.item_type !== `narration`) || message.role !== `assistant`) {
        return false;
      }

      return Boolean(message.content?.trim());
    });
  }, [conversationDetail?.transcript, latestResolveCheckRun]);

  useEffect(() => {
    if (!pendingResolveCheckRunId) {
      return;
    }

    const queuedRun = (conversationDetail?.runs || []).find(run => run.id === pendingResolveCheckRunId);

    if (!queuedRun) {
      return;
    }

    if (RESOLVE_CHECK_TERMINAL_RUN_STATUSES.has(queuedRun.status)) {
      setPendingResolveCheckRunId(null);
    }
  }, [conversationDetail?.runs, pendingResolveCheckRunId]);

  const transcriptItems = useMemo(() => {
    const transcript = conversationDetail?.transcript || [];
    const notes = conversationDetail?.notes || [];
    const tasksByCallId = new Map((conversationDetail?.tasks || []).map(task => [task.call_id, task]));
    const messageItems: MessageTranscriptItem[] = [];
    const firstUserMessageRunIds = new Set<string>();

    for (let i = 0; i < transcript.length; i += 1) {
      const message = transcript[i];

      if (message.item_type === `function_call`) {
        const task = message.call_id ? tasksByCallId.get(message.call_id) : undefined;
        const maybeOutput = transcript[i + 1];
        const matchedOutput = maybeOutput
          && maybeOutput.item_type === `function_call_output`
          && maybeOutput.call_id
          && message.call_id
          && maybeOutput.call_id === message.call_id ?
          maybeOutput :
          undefined;
        const mergedMessage = matchedOutput ?
          { ...message, output: matchedOutput.output } :
          message;
        const activity = describeToolActivity(mergedMessage);

        messageItems.push({
          kind: `message`,
          key: `tool-${message.id}`,
          created_at: mergedMessage.created_at,
          message: mergedMessage,
          activity,
          medium: activity.medium,
          task,
          attachments: [],
        });

        if (matchedOutput) {
          i += 1;
        }

        continue;
      }

      if (message.item_type === `function_call_output`) {
        continue;
      }

      const run = message.run_id ? runsById.get(message.run_id) : undefined;
      const medium = message.role === `user` ?
        run?.trigger_type === `inbound_email` ?
          `email` :
          run?.trigger_type === `inbound_sms` ?
            `sms` :
            undefined :
        undefined;
      const isFirstUserMessageForRun = Boolean(
        message.role === `user`
        && message.run_id
        && !firstUserMessageRunIds.has(message.run_id),
      );

      if (message.role === `user` && message.run_id) {
        firstUserMessageRunIds.add(message.run_id);
      }

      messageItems.push({
        kind: `message`,
        key: `message-${message.id}`,
        created_at: message.created_at,
        message,
        medium,
        customerLabel: message.role === `user` || message.role === `staff` ? run?.sender || undefined : undefined,
        inboundEmailId: message.role === `user` ? run?.inbound_email_id : undefined,
        attachments: isFirstUserMessageForRun ? run?.attachments || [] : [],
      });
    }

    const noteItems: NoteTranscriptItem[] = notes.map(note => ({
      kind: `note`,
      key: `note-${note.id}`,
      created_at: note.created_at,
      note,
    }));
    const mergedItems = [...messageItems, ...noteItems]
      .map((item, index) => ({ item, index }))
      .sort((a, b) => {
        const aTime = new Date(a.item.created_at).getTime();
        const bTime = new Date(b.item.created_at).getTime();

        if (aTime === bTime) {
          return a.index - b.index;
        }

        return aTime - bTime;
      })
      .map(({ item }) => item);

    return mergedItems;
  }, [conversationDetail?.notes, conversationDetail?.tasks, conversationDetail?.transcript, runsById]);

  const unmatchedPendingTasks = useMemo(() => {
    const inlineRenderedTaskIds = new Set(
      transcriptItems
        .filter((item): item is MessageTranscriptItem => item.kind === `message`)
        .filter(item => item.task?.approval_status === `pending`)
        .map(item => item.task?.id),
    );

    return pendingTasks.filter(task => !inlineRenderedTaskIds.has(task.id));
  }, [transcriptItems, pendingTasks]);

  const handleTaskDecision = async(task: AgentTask, approved: boolean, autoApproveTool?: boolean) => {
    if (!organisationId || !selectedConversationId) {
      return false;
    }

    setPendingDecisionTaskId(task.id);

    setProcessingTaskIds(prev => {
      const next = new Set(prev);

      next.add(task.id);

      return next;
    });

    try {
      const editableFields = getEditableFieldsForTool(task.tool_name);
      const taskEdit = taskEdits[task.id] || getInitialTaskEditValues(task);
      const editedArgs = approved && editableFields.length ? buildEditedArgsForTask(task, taskEdit) : undefined;

      await decideTask({
        organisationId,
        conversationId: selectedConversationId,
        taskId: task.id,
        approved,
        autoApproveTool,
        editedArgs,
      }).unwrap();

      return true;
    }
    catch {
      setProcessingTaskIds(prev => {
        if (!prev.has(task.id)) {
          return prev;
        }

        const next = new Set(prev);

        next.delete(task.id);

        return next;
      });

      return false;
    }
    finally {
      setPendingDecisionTaskId(null);
    }
  };

  const handleSkipAndResolve = async(task: AgentTask) => {
    const skipped = await handleTaskDecision(task, false);

    if (!skipped || !organisationId || !selectedConversationId) {
      return;
    }

    await resolveConversation({
      organisationId,
      conversationId: selectedConversationId,
    });
  };

  const handleResolveConversation = async() => {
    if (!organisationId || !selectedConversationId) {
      return;
    }

    await resolveConversation({
      organisationId,
      conversationId: selectedConversationId,
    });
  };

  const handleResolveConversationFromList = async(conversationId: string) => {
    if (!organisationId) {
      return;
    }

    setResolvingListConversationId(conversationId);

    try {
      await resolveConversation({
        organisationId,
        conversationId,
      });
    }
    finally {
      setResolvingListConversationId(null);
    }
  };

  const handleCheckResolved = async() => {
    if (!organisationId || !selectedConversationId || Boolean(conversationDetail?.conversation.archived_at)) {
      return;
    }

    try {
      const response = await checkResolved({
        organisationId,
        conversationId: selectedConversationId,
      }).unwrap();

      setPendingResolveCheckRunId(response.runId);
    }
    catch {
      setPendingResolveCheckRunId(null);
    }
  };

  const handleArchiveToggle = async() => {
    if (!organisationId || !selectedConversationId || !conversationDetail) {
      return;
    }

    if (conversationDetail.conversation.archived_at) {
      await unarchiveConversation({
        organisationId,
        conversationId: selectedConversationId,
      });

      return;
    }

    await archiveConversation({
      organisationId,
      conversationId: selectedConversationId,
    });
  };

  const handleAgentEnabledChange = async(enabled: boolean) => {
    if (!organisationId) {
      return;
    }

    await updateAgentSettings({
      organisationId,
      enabled,
    });
  };

  if (!organisationId) {
    return null;
  }

  const selectedStatusMeta = conversationDetail ? getConversationStatusMeta(conversationDetail.conversation.status) : null;
  const isConversationArchived = Boolean(conversationDetail?.conversation.archived_at);
  const isTerminalConversationStatus = conversationDetail ?
    [ConversationStatus.RESOLVED, ConversationStatus.CLOSED].includes(conversationDetail.conversation.status) :
    false;
  const isArchivingToggle = isArchivingConversation || isUnarchivingConversation;
  const isResolveCheckRunning = latestResolveCheckRun ?
    !RESOLVE_CHECK_TERMINAL_RUN_STATUSES.has(latestResolveCheckRun.status) :
    false;
  const resolveCheckOutcomeLabel = conversationDetail &&
    [ConversationStatus.RESOLVED, ConversationStatus.CLOSED].includes(conversationDetail.conversation.status) ?
    `Resolved` :
    `Still open`;
  const parsedConversationSummary = parseConversationSummary(conversationDetail?.conversation.summary);
  // const showDecisionPanel = Boolean(parsedConversationSummary.summary || parsedConversationSummary.decisionSteps.length > 0);
  const showDecisionPanel = false;
  const sortOptions: Array<{ value: SortOptionValue; label: string }> = [
    { value: `last_activity_at:desc`, label: `Sort by: Recent activity` },
    { value: `last_activity_at:asc`, label: `Sort by: Oldest activity` },
    { value: `opened_at:desc`, label: `Sort by: Recently opened` },
    { value: `opened_at:asc`, label: `Sort by: Oldest opened` },
    { value: `created_at:desc`, label: `Sort by: Recently created` },
    { value: `created_at:asc`, label: `Sort by: Oldest created` },
    { value: `status:asc`, label: `Sort by: Status (A to Z)` },
    { value: `status:desc`, label: `Sort by: Status (Z to A)` },
  ];
  const selectedSortOptionValue: SortOptionValue = `${sortBy}:${sortDir}`;

  return (
    <div className={ `space-y-4` }>
      <div className={ `flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between` }>
        <div className={ `space-y-1` }>
          <div className={ `flex items-center gap-2` }>
            <Paragraph>
              { `AI Agent Conversations` }
            </Paragraph>
            <Tooltip title={ `You are in early access for Agents. Please contact support if you have any ideas, questions, or concerns.` }>
              <Badge
                color={ `purple` }
                message={ `Early Access` }
              />
            </Tooltip>
          </div>
          <Paragraph
            size={ `sm` }
            variant={ `secondary` }
          >
            { `Conversations the AI agent is handling with your customers.` }
          </Paragraph>
        </div>
        <div className={ `w-full rounded-lg border border-gray-200 bg-white px-3 py-2 sm:max-w-sm` }>
          <Toggle
            label={ `AI agent enabled` }
            checked={ agentSettings?.enabled || false }
            loading={ isAgentSettingsLoading || isUpdatingAgentSettings }
            disabled={ !agentSettings || isUpdatingAgentSettings }
            onChange={ ({ value }) => handleAgentEnabledChange(value) }
          />
        </div>
      </div>

      {
        (needsApprovalCount > 0 || needsHumanCount > 0 || failedCount > 0) &&
        <Notification
          type={ failedCount > 0 ? `error` : `warning` }
          title={ `Some conversations need you` }
        >
          <div className={ `flex flex-wrap items-center gap-x-1` }>
            {
              needsApprovalCount > 0 &&
              <button
                type={ `button` }
                className={ `underline underline-offset-2 hover:no-underline` }
                onClick={ () => setStatusFilters([ConversationStatus.AWAITING_APPROVAL]) }
              >
                { `${needsApprovalCount} awaiting your approval` }
              </button>
            }
            {
              needsApprovalCount > 0 && (needsHumanCount > 0 || failedCount > 0) &&
              <span>{ `·` }</span>
            }
            {
              needsHumanCount > 0 &&
              <button
                type={ `button` }
                className={ `underline underline-offset-2 hover:no-underline` }
                onClick={ () => setStatusFilters([ConversationStatus.NEEDS_HUMAN]) }
              >
                { `${needsHumanCount} marked as needs a human` }
              </button>
            }
            {
              needsHumanCount > 0 && failedCount > 0 &&
              <span>{ `·` }</span>
            }
            {
              failedCount > 0 &&
              <button
                type={ `button` }
                className={ `underline underline-offset-2 hover:no-underline` }
                onClick={ () => setStatusFilters([`failed`]) }
              >
                { `${failedCount} with a failed action` }
              </button>
            }
          </div>
        </Notification>
      }

      <div className={ `grid grid-cols-1 gap-4 lg:grid-cols-3` }>
        <div className={ `overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xs lg:col-span-1 lg:max-h-[calc(100vh-220px)]` }>
          <div className={ `border-b border-gray-200 px-4 py-3` }>
            <Paragraph>
              { `Conversations` }
            </Paragraph>
          </div>

          <div className={ `border-b border-gray-100 px-4 py-2.5` }>
            <div className={ `grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]` }>
              <select
                aria-label={ `Sort by` }
                className={ `w-full rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-700 focus:border-paidniceGreen-300 focus:outline-hidden` }
                value={ selectedSortOptionValue }
                onChange={ event => {
                  const [nextSortBy, nextSortDir] = event.target.value.split(`:`) as [AgentConversationSortBy, AgentConversationSortDir];

                  setSortBy(nextSortBy);
                  setSortDir(nextSortDir);
                } }
              >
                {
                  sortOptions.map(option => (
                    <option
                      key={ option.value }
                      value={ option.value }
                    >
                      { option.label }
                    </option>
                  ))
                }
              </select>
              <MultiSelect
                selected={ statusFilters }
                options={ statusFilterOptions }
                display={ statusFilterDisplay }
                onChange={ value => updateStatusFilters(value as Array<StatusFilterValue | typeof CLEAR_ALL_STATUS_FILTERS_VALUE>) }
                size={ `sm` }
                containerClassname={ `min-h-0` }
                listButtonClassname={ `flex h-[30px] items-center rounded-md border-gray-200 text-xs text-gray-700` }
                valueClassname={ `w-full font-normal leading-none text-gray-700` }
              />
              {
                !isDefaultStatusFilters &&
                <Tooltip title={ `Clear filters` }>
                  <button
                    type={ `button` }
                    aria-label={ `Clear filters` }
                    className={ `flex h-[30px] w-[30px] items-center justify-center rounded-md border border-gray-200 bg-white text-paidniceGreen-700 transition-colors hover:border-gray-300 hover:text-paidniceGreen-800` }
                    onClick={ () => setStatusFilters(DEFAULT_STATUS_FILTERS) }
                  >
                    <XMarkIcon className={ `h-4 w-4` } />
                  </button>
                </Tooltip>
              }
            </div>
          </div>

          <LoadingOverlay
            loading={ isConversationsLoading }
            className={ `min-h-[160px]` }
          >
            <div className={ `space-y-1 p-2 lg:max-h-[calc(100vh-360px)] lg:overflow-y-auto` }>
              {
                !isConversationsLoading && !!conversationsError &&
                <Paragraph
                  variant={ `error` }
                  className={ `p-4` }
                >
                  { `Couldn't load conversations.` }
                </Paragraph>
              }
              {
                !isConversationsLoading && !conversations.length &&
                <Paragraph
                  variant={ `secondary` }
                  className={ `p-4` }
                >
                  { `No conversations yet.` }
                </Paragraph>
              }
              {
                conversations.map(agentConversation => (
                  <ConversationListItem
                    key={ agentConversation.id }
                    agentConversation={ agentConversation }
                    selected={ agentConversation.id === selectedConversationId }
                    onSelect={ selectConversation }
                    onResolve={ handleResolveConversationFromList }
                    isResolving={ isResolvingConversation && resolvingListConversationId === agentConversation.id }
                  />
                ))
              }
            </div>
          </LoadingOverlay>

          {
            !!pagination && pagination.totalPages > 1 &&
            <div className={ `flex items-center justify-between border-t border-gray-100 px-4 py-3` }>
              <Button
                color={ `gray` }
                size={ `sm` }
                disabled={ page <= 1 }
                onClick={ () => setPage(prev => Math.max(prev - 1, 1)) }
              >
                { `Previous` }
              </Button>
              <Paragraph
                size={ `xs` }
                variant={ `secondary` }
              >
                { `Page ${pagination.page} of ${pagination.totalPages}` }
              </Paragraph>
              <Button
                color={ `gray` }
                size={ `sm` }
                disabled={ pagination.page >= pagination.totalPages }
                onClick={ () => setPage(prev => prev + 1) }
              >
                { `Next` }
              </Button>
            </div>
          }
        </div>

        <div className={ `lg:col-span-2 lg:max-h-[calc(100vh-220px)] lg:overflow-y-auto lg:pr-1` }>
          <LoadingOverlay
            loading={ !!selectedConversationId && isConversationLoading }
            className={ `min-h-[200px]` }
          >
            {
              !selectedConversationId &&
              <div className={ `flex h-full items-center justify-center rounded-lg border border-dashed border-gray-200 bg-white p-10 shadow-xs` }>
                <Paragraph variant={ `secondary` }>
                  { `Select a conversation to see the details.` }
                </Paragraph>
              </div>
            }

            {
              !!selectedConversationId && !!conversationError &&
              <Paragraph variant={ `error` }>
                { `Couldn't load this conversation.` }
              </Paragraph>
            }

            {
              !!conversationDetail && !!selectedStatusMeta &&
              <div className={ `space-y-4` }>
                <div className={ `flex items-start justify-between gap-3 rounded-xl border border-gray-200 bg-linear-to-r from-white to-gray-50/70 p-4 shadow-xs` }>
                  <div className={ `flex items-start gap-3` }>
                    <ContactAvatar name={ conversationDetail.conversation.contact_name } />
                    <div>
                      <div className={ `flex flex-wrap items-center gap-2` }>
                        <Paragraph
                          variant={ `link` }
                        >
                          {
                            conversationDetail.conversation.contact_id ?
                              <WrappedLink
                                to={ `/contacts/${conversationDetail.conversation.contact_id}` }
                                className={ `hover:underline` }
                              >
                                { conversationDetail.conversation.contact_name || `Unknown contact` }
                              </WrappedLink> :
                              (conversationDetail.conversation.contact_name || `Unknown contact`)
                          }
                        </Paragraph>
                        {
                          conversationDetail.conversation.invoice_id
                          && conversationDetail.conversation.invoice_number &&
                          <WrappedLink
                            to={ `/invoices/${conversationDetail.conversation.invoice_id}` }
                            className={ `rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs text-gray-500 transition hover:border-gray-300 hover:bg-gray-100 hover:text-gray-700` }
                          >
                            { conversationDetail.conversation.invoice_number }
                          </WrappedLink>
                        }
                        <Badge
                          color={ selectedStatusMeta.color }
                          className={ `rounded-full px-2.5 py-1` }
                        >
                          <span>{ selectedStatusMeta.label }</span>
                        </Badge>
                        {
                          isConversationArchived &&
                          <Badge
                            color={ `gray` }
                            className={ `gap-1 rounded-full px-2.5 py-1` }
                          >
                            <ArchiveBoxIcon className={ `h-3.5 w-3.5` } />
                            <span>{ `Archived` }</span>
                          </Badge>
                        }
                      </div>
                      <Paragraph
                        size={ `xs` }
                        variant={ `secondary` }
                        className={ `mt-1` }
                      >
                        { parsedConversationSummary.summary || `No summary yet` }
                      </Paragraph>
                    </div>
                  </div>
                  <div className={ `flex items-start justify-end` }>
                    <div
                      className={ `relative` }
                      ref={ conversationActionsMenuRef }
                    >
                      <button
                        type={ `button` }
                        aria-haspopup={ `menu` }
                        aria-expanded={ isConversationActionsMenuOpen }
                        onClick={ () => setIsConversationActionsMenuOpen(prev => !prev) }
                        className={ `flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 shadow-xs transition hover:border-gray-300 hover:bg-gray-50` }
                      >
                        <EllipsisHorizontalIcon className={ `h-4 w-4` } />
                      </button>
                      {
                        isConversationActionsMenuOpen &&
                      <div className={ `absolute right-0 z-10 mt-2 min-w-[220px] overflow-hidden rounded-lg border border-gray-200 bg-white p-1 shadow-lg` }>
                        {
                          !isTerminalConversationStatus &&
                          <button
                            type={ `button` }
                            className={ `flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50` }
                            disabled={ isResolvingConversation }
                            onClick={ async() => {
                              setIsConversationActionsMenuOpen(false);
                              await handleResolveConversation();
                            } }
                          >
                            <CheckCircleIcon className={ `h-4 w-4 text-paidniceGreen-600` } />
                            <span>{ isResolvingConversation ? `Marking as resolved...` : `Mark as resolved` }</span>
                          </button>
                        }
                        {
                          !isTerminalConversationStatus &&
                          <button
                            type={ `button` }
                            className={ `flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50` }
                            disabled={ isConversationArchived || isCheckingResolved || isResolveCheckRunning }
                            onClick={ async() => {
                              setIsConversationActionsMenuOpen(false);
                              await handleCheckResolved();
                            } }
                          >
                            <ArrowPathIcon className={ `h-4 w-4 text-blue-600` } />
                            <span>{ isCheckingResolved || isResolveCheckRunning ? `Re-checking...` : `Check for resolved` }</span>
                          </button>
                        }
                        <button
                          type={ `button` }
                          className={ `flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50` }
                          disabled={ isArchivingToggle }
                          onClick={ async() => {
                            setIsConversationActionsMenuOpen(false);
                            await handleArchiveToggle();
                          } }
                        >
                          <ArchiveBoxIcon className={ `h-4 w-4 text-gray-500` } />
                          <span>{ isConversationArchived ? `Unarchive conversation` : `Archive conversation` }</span>
                        </button>
                      </div>
                      }
                    </div>
                  </div>
                </div>

                {
                  latestResolveCheckRun &&
                  <div className={ `rounded-lg border border-gray-200 bg-gray-50 p-3` }>
                    <div className={ `flex flex-wrap items-center gap-2` }>
                      <Paragraph size={ `sm` }>
                        { `Latest Check for Resolved` }
                      </Paragraph>
                      <Badge color={ resolveCheckOutcomeLabel === `Resolved` ? `green` : `blue` }>
                        { resolveCheckOutcomeLabel }
                      </Badge>
                      <span className={ `text-xs text-gray-400` }>
                        { formatLocalTimestamp(latestResolveCheckRun.started_at || latestResolveCheckRun.created_at) }
                      </span>
                    </div>
                    {
                      resolveCheckAssistantMessages.length > 0 &&
                      <div className={ `mt-2 space-y-2` }>
                        {
                          resolveCheckAssistantMessages.map(message => (
                            <p
                              key={ `resolve-check-message-${message.id}` }
                              className={ `whitespace-pre-wrap rounded-md border border-gray-200 bg-white p-2 text-sm text-gray-700` }
                            >
                              { message.content }
                            </p>
                          ))
                        }
                      </div>
                    }
                    {
                      !resolveCheckAssistantMessages.length &&
                      <Paragraph
                        size={ `xs` }
                        variant={ `secondary` }
                        className={ `mt-2` }
                      >
                        { `No assistant assessment message was recorded for this check.` }
                      </Paragraph>
                    }
                  </div>
                }

                {
                  failedRuns.length > 0 &&
                <Notification
                  type={ `error` }
                  title={ `An action didn't complete` }
                >
                  <div className={ `space-y-1` }>
                    {
                      failedRuns.map(run => (
                        <p key={ run.id }>
                          { run.error }
                        </p>
                      ))
                    }
                  </div>
                </Notification>
                }

                {
                  SHOW_RUNS &&
                  <div className={ `rounded-lg border border-gray-200 bg-white p-4 shadow-xs` }>
                    <div className={ `mb-3 flex items-center gap-2` }>
                      <ArrowPathIcon className={ `h-5 w-5 text-violet-500` } />
                      <Paragraph>
                        { `Runs` }
                      </Paragraph>
                    </div>
                    {
                      !sortedRuns.length &&
                      <Paragraph
                        size={ `xs` }
                        variant={ `secondary` }
                      >
                        { `No runs yet.` }
                      </Paragraph>
                    }
                    <div className={ `space-y-3` }>
                      {
                        sortedRuns.map(run => {
                          const statusMeta = getRunStatusMeta(run.status);

                          return (
                            <div
                              key={ run.id }
                              className={ `rounded-md border border-gray-200 bg-gray-50 p-3` }
                            >
                              <div className={ `flex flex-wrap items-center justify-between gap-2` }>
                                <Paragraph size={ `sm` }>
                                  { friendlyTriggerType(run.trigger_type) }
                                </Paragraph>
                                <Badge color={ statusMeta.color }>
                                  { statusMeta.label }
                                </Badge>
                              </div>
                              <div className={ `mt-2 flex flex-wrap items-center gap-3` }>
                                <span className={ `text-xs text-gray-400` }>
                                  { formatLocalTimestamp(run.started_at || run.created_at) }
                                </span>
                                {
                                  run.finished_at &&
                                  <span className={ `text-xs text-gray-400` }>
                                    { formatLocalTimestamp(run.finished_at) }
                                  </span>
                                }
                              </div>
                              {
                                run.error &&
                                <Paragraph
                                  size={ `xs` }
                                  variant={ `error` }
                                  className={ `mt-2` }
                                >
                                  { run.error }
                                </Paragraph>
                              }
                            </div>
                          );
                        })
                      }
                    </div>
                  </div>
                }

                <div className={ `rounded-lg border border-gray-200 bg-white p-4 shadow-xs` }>
                  <div className={ `mb-3 flex items-center gap-2` }>
                    <ChatBubbleLeftRightIcon className={ `h-5 w-5 text-paidniceGreen-500` } />
                    <Paragraph>
                      { `Messages` }
                    </Paragraph>
                  </div>
                  {
                    conversationDetail.originalOutboundMessage &&
                    <div className={ `mb-3` }>
                      <OriginalOutboundMessage
                        message={ conversationDetail.originalOutboundMessage }
                        sourceActionId={ conversationDetail.conversation.source_action_id }
                        onViewAction={ actionId => dispatch(openActionDrawer(actionId)) }
                      />
                    </div>
                  }
                  {
                    !transcriptItems.length &&
                  <Paragraph
                    size={ `xs` }
                    variant={ `secondary` }
                  >
                    { `Nothing has been said yet.` }
                  </Paragraph>
                  }
                  <div className={ `space-y-3` }>
                    {
                      transcriptItems.map(item => {
                        if (item.kind === `note`) {
                          return (
                            <TranscriptItem
                              key={ item.key }
                              kind={ `note` }
                              note={ item.note }
                            />
                          );
                        }

                        return (
                          <TranscriptItem
                            key={ item.key }
                            message={ item.message }
                            activity={ item.activity }
                            customerLabel={ item.customerLabel }
                            actionId={ item.task?.action_id || item.activity?.actionId }
                            invoiceId={ item.task?.invoice_id || null }
                            task={ item.task }
                            taskEdits={ taskEdits }
                            setTaskEdits={ setTaskEdits }
                            isDecidingTask={ isDecidingTask }
                            pendingDecisionTaskId={ pendingDecisionTaskId }
                            isProcessing={ Boolean(item.task && processingTaskIds.has(item.task.id)) }
                            onDecideTask={ handleTaskDecision }
                            onSkipAndResolve={ handleSkipAndResolve }
                            onViewAction={ actionId => dispatch(openActionDrawer(actionId)) }
                            attachments={ item.attachments }
                            inboundEmailId={ item.inboundEmailId || null }
                          />
                        );
                      })
                    }
                  </div>
                  {
                    unmatchedPendingTasks.length > 0 &&
                    <div className={ `mt-3 space-y-3` }>
                      {
                        unmatchedPendingTasks.map(task => (
                          <div
                            key={ task.id }
                            className={ `rounded-lg border border-orange-200 bg-orange-50/40 p-3` }
                          >
                            <PendingTaskApproval
                              task={ task }
                              taskEdits={ taskEdits }
                              setTaskEdits={ setTaskEdits }
                              isDecidingTask={ isDecidingTask }
                              pendingDecisionTaskId={ pendingDecisionTaskId }
                              isProcessing={ processingTaskIds.has(task.id) }
                              onDecideTask={ handleTaskDecision }
                              onSkipAndResolve={ handleSkipAndResolve }
                            />
                          </div>
                        ))
                      }
                    </div>
                  }
                  {
                    selectedConversationId &&
                    <ConversationComposer
                      organisationId={ organisationId }
                      conversationId={ selectedConversationId }
                      isTerminalConversationStatus={ isTerminalConversationStatus }
                      isConversationArchived={ isConversationArchived }
                      isResolvingConversation={ isResolvingConversation }
                      onResolveConversation={ handleResolveConversation }
                      currentOrgUsers={ currentOrgUsers }
                      chatDisabled={ isTerminalConversationStatus || isConversationArchived }
                      chatDisabledReason={ `Chat is unavailable on resolved or archived conversations.` }
                    />
                  }
                  <div ref={ transcriptBottomAnchorRef } />
                </div>
                {
                  showDecisionPanel &&
                  <div className={ `rounded-lg border border-amber-200 bg-amber-50/40 p-4 shadow-xs` }>
                    <button
                      type={ `button` }
                      className={ `flex w-full items-center justify-between gap-2 text-left` }
                      aria-expanded={ isDecisionPanelOpen }
                      onClick={ () => setIsDecisionPanelOpen(prev => !prev) }
                    >
                      <span className={ `flex items-center gap-2` }>
                        <LightBulbIcon className={ `h-5 w-5 text-amber-500` } />
                        <Paragraph>
                          { `What the agent decided` }
                        </Paragraph>
                      </span>
                      <ChevronDownIcon
                        className={ classNames(
                          `h-4 w-4 text-amber-700 transition-transform`,
                          isDecisionPanelOpen && `rotate-180`,
                        ) }
                      />
                    </button>
                    {
                      isDecisionPanelOpen && !!parsedConversationSummary.summary &&
                      <Paragraph
                        size={ `sm` }
                        className={ `mt-2 text-amber-900` }
                      >
                        { parsedConversationSummary.summary }
                      </Paragraph>
                    }
                    {
                      isDecisionPanelOpen && parsedConversationSummary.decisionSteps.length > 0 &&
                      <ol className={ `mt-2 list-decimal space-y-1 pl-5 text-sm text-amber-900` }>
                        {
                          parsedConversationSummary.decisionSteps.map((step, index) => (
                            <li key={ `${step}-${index}` }>
                              { step }
                            </li>
                          ))
                        }
                      </ol>
                    }
                  </div>
                }
              </div>
            }
          </LoadingOverlay>
        </div>
      </div>
    </div>
  );
}
